import { Component, DestroyRef, inject, effect, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { SettingsService } from 'src/app/shared/services/settings';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { workflowSchema } from '../../constants/schemas';
import { DynamicActionFormRendererComponent } from 'src/app/shared/components/dynamic-action-form-renderer/dynamic-action-form-renderer.component';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import * as _ from 'lodash';
import * as moment from 'moment';

// Declare Helpers globally
declare const Helpers: any;
declare const Utils: any;

interface Workflow {
  uuid: string;
  name: string;
  description?: string;
  is_enabled: boolean;
  active: boolean; // Alias for is_enabled for template
  created_date: Date | string;
  last_updated_on?: Date | string;
  last_updated_by?: string;
  updated_by?: string;
  formatted_created_date?: string;
  createdDate?: Date; // For template compatibility
  lastUpdated?: Date; // For template compatibility
  updatedBy?: string; // For template compatibility
  is_deleted?: boolean;
  event_id_list?: string[];
  event_name?: string;
  delay_time?: number; // UI-only minutes; maps to trigger_time_in_ms
  trigger_time_in_ms?: number;
  conditions?: any[];
  conditions_v2?: any;
  actions?: any[];
  brand_id?: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  start: number;
  end: number;
  total: number;
  rulePageNo: number;
  statusRulesPageFrom: number;
  statusRulesPageTo: number;
  statusRulesCount: number;
  limit: number;
  disableNext: boolean;
  disablePrev: boolean;
}

@Component({
  selector: 'app-litmus-action-workflows',
  templateUrl: './litmus-action-workflows.component.html',
  styleUrls: ['./litmus-action-workflows.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicActionFormRendererComponent, LtmsLoaderComponent]
})
export class LitmusActionWorkflowsComponent implements OnInit, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);

  // Loading and state management
  pagerBusy = true;
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;

  // Brand and configuration
  brandId: string = '';
  brandDetails: any = {};
  configurationLoaded: boolean = false;
  workflowSchema = _.cloneDeep(workflowSchema);
  // Data storage
  workflows: Workflow[] = [];
  completeWorkflow: Workflow[] = [];
  workflowListToSearchInto: Workflow[] = [];
  workflowHash: any = {};
  managersMapping: Map<string, string> = new Map();

  // Search
  searchText: string = '';
  lwActionWorkflowSearchInput: string = '';

  // Pagination
  pagination: Pagination = {
    currentPage: 1,
    totalPages: 1,
    start: 0,
    end: 0,
    total: 0,
    rulePageNo: 1,
    statusRulesPageFrom: 0,
    statusRulesPageTo: 0,
    statusRulesCount: 0,
    limit: 10,
    disableNext: true,
    disablePrev: true
  };

  pagingButtons = {
    isNext: false,
    isPrevious: false,
    isLast: false,
    isFirst: false
  };

  get isConfigurationReady(): boolean {
    return this.configurationLoaded && !this.pagerBusy;
  }

  // Sorting
  dateByAsc: boolean = true;
  nameByAsc: boolean = true;

  // Filtered workflows for display
  filteredWorkflows: Workflow[] = [];
  ruleSetIdsToExclude: string[] = ['lw_manager_fields_logic_ruleset','response_fields_logic_mandatory_ruleset','lw_manager_fields_logic_ruleset'];

  // Modal
  isModalOpen: boolean = false;
  modalSchema: any = {};
  defaultValues: any = {};
  modalMode: 'insert' | 'update' = 'insert';
  selectedWorkflow: Workflow | null = null;
  indexOfWorkflowToEdit: number = -1;
  conditionsV2Enabled: boolean = false;

  constructor(
    private _toast: ToastService,
    private stateService: AppStateService,
    private _settingsService: SettingsService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        if (!_.isEmpty(brandDetail) && brandDetail._id) {
          this.brandDetails = brandDetail;
          this.brandId = brandDetail._id;
          this.conditionsV2Enabled = !!_.get(brandDetail, 'action_management_configuration.is_conditions_v2_enabled');

          // Matching old conditions-config: build fieldDefsMap from brand field_definitions
          this.fieldDefsMap = {};
          if (brandDetail.field_definitions && !_.isEmpty(brandDetail.field_definitions)) {
            brandDetail.field_definitions.forEach((field: any) => {
              if (field?.field_id) {
                this.fieldDefsMap[field.field_id] = field;
              }
            });
          }

          this.getEventList().then(() => {
            this.getWorkflowRules().then(() => {
              this.initializeComponent();
              this.configurationLoaded = true;
            });
          });
         
        } else {
          console.warn('LitmusActionWorkflowsComponent: Brand details not available yet');
        }
      }
    });
  }

  ngOnInit() {
    this.resetData();
  }

  /**
   * Reset all data to initial state
   */
  private resetData(): void {
    this.pagination = {
      currentPage: 1,
      totalPages: 1,
      start: 0,
      end: 0,
      total: 0,
      rulePageNo: 1,
      statusRulesPageFrom: 0,
      statusRulesPageTo: 0,
      statusRulesCount: 0,
      limit: 10,
      disableNext: true,
      disablePrev: true
    };
    this.searchText = '';
    this.lwActionWorkflowSearchInput = '';
    this.workflows = [];
    this.completeWorkflow = [];
    this.workflowListToSearchInto = [];
    this.workflowHash = {};
    this.managersMapping = new Map();
    this.dateByAsc = true;
    this.nameByAsc = true;
    this.pagingButtons = {
      isNext: false,
      isPrevious: false,
      isLast: false,
      isFirst: false
    };
  }

  /**
   * Initialize component - matching old AngularJS intializeComponent
   */
  private initializeComponent(): void {
    this.pagerBusy = true;
    this.resetData();
    this.getWorkflowList(this.brandId);
  }

  /**
   * Get workflow list from API - matching old AngularJS getWorkflowList
   */
  private getWorkflowList(brandId: string): void {
    if (!brandId) {
      console.warn('getWorkflowList: brandId is missing');
      this.pagerBusy = false;
      return;
    }

    this.pagerBusy = true;

    this._settingsService.getWorkflowList(brandId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.pagerBusy = false;

          // Handle different response structures
          // Old AngularJS: response.data.managers && response.data.rules
          // The Observable might return response.data directly or the full response
          let managers: any[] = [];
          let rules: any[] = [];

          // Check if response is the data directly (common in Angular services)
          if (response && response.managers && response.rules) {
            managers = response.managers || [];
            rules = response.rules || [];
          } else if (response && response.data) {
            // Check if response.data has managers and rules
            if (response.data.managers && response.data.rules) {
              managers = response.data.managers || [];
              rules = response.data.rules || [];
            } else if (response.data.rules) {
              // Only rules, no managers
              rules = response.data.rules || [];
            } else if (Array.isArray(response.data)) {
              // Response might be directly an array
              rules = response.data;
            } else if (response.data.data) {
              // Nested data structure
              if (response.data.data.managers && response.data.data.rules) {
                managers = response.data.data.managers || [];
                rules = response.data.data.rules || [];
              } else if (response.data.data.rules) {
                rules = response.data.data.rules || [];
              } else if (Array.isArray(response.data.data)) {
                rules = response.data.data;
              }
            }
          } else if (Array.isArray(response)) {
            // Response is directly an array
            rules = response;
          }


          // Create managers mapping - matching old AngularJS logic
          this.managersMapping = new Map();
          managers.forEach((manager: any) => {
            if (manager && manager._id) {
              const managerName = manager.name || manager.email_address || '';
              this.managersMapping.set(manager._id, managerName);
            }
          });

          // Prepare workflow list
          if (rules && rules.length > 0) {
            this.prepareWorkflowList(_.cloneDeep(rules));
          } else {
            console.warn('getWorkflowList: No rules found in response');
            console.warn('getWorkflowList: Response structure:', Object.keys(response || {}));
            this.workflows = [];
            this.completeWorkflow = [];
            this.workflowListToSearchInto = [];
            this.filteredWorkflows = [];
            this.pagination.statusRulesCount = 0;
            this.pagination.start = 0;
            this.pagination.end = 0;
            this.pagination.total = 0;
          }
        },
        error: (error: any) => {
          console.error('Error fetching workflow list:', error);
          this.pagerBusy = false;
          this.workflows = [];
          this.completeWorkflow = [];
          this.workflowListToSearchInto = [];
          this.filteredWorkflows = [];
        }
      });
  }


  ltmsEventList: any[] = [];
  ltmsEventListHash: any = {};
  private getEventList() {
    return new Promise((resolve) => {
      this._settingsService.getWorkflowRules(this.brandId, 1)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.ltmsEventList = [];
            this.ltmsEventListHash = {};
            if (response && response.data?.code === 0 && _.has(response, 'data.data') && response.data?.data?.length > 0) {
              let events: any[] = response?.data?.data || [];

              this.ltmsEventList = _.map(events, (event) => {
                event["id"] = event["_id"];
                event["text"] = event["name"];
                this.ltmsEventListHash[event["_id"]] = event;
                return event;
              });
            }

            resolve(true);
          },
          error: (error: any) => {
            console.error('Error fetching event list:', error);
            this.ltmsEventList = [];
            this.ltmsEventListHash = {};
            resolve(true);
          }
        });
    })

  }



  workflowFields: any[] = [];
  workflowOperators: any[] = [];
  rulesetHash: any = {};
  fieldDefsMap: any = {};

   /**
   * Get workflow rules for conditions
   */
   getWorkflowRules(): Promise<void> {
    return new Promise((resolve) => {
      this._settingsService.getWorkflowRules(this.brandId, 3)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            this.workflowFields = [];
            this.workflowOperators = [];
            this.rulesetHash = {};
            // Do NOT clear fieldDefsMap here — it is built from brand field_definitions
            // and required by getExternalFields / setRuleSetObj

            let rulesets: any[] = [];
            if (response && response.data) {
              if (Array.isArray(response.data)) {
                rulesets = response.data;
              } else if (response.data.data && Array.isArray(response.data.data)) {
                rulesets = response.data.data;
              } else if (response.data.rulesets && Array.isArray(response.data.rulesets)) {
                rulesets = response.data.rulesets;
              }
            }

            // Filter out excluded rulesets
            if (this.ruleSetIdsToExclude && this.ruleSetIdsToExclude.length) {
              rulesets = _.filter(rulesets, (ruleSet: any) => {
                return !(ruleSet._id && this.ruleSetIdsToExclude.indexOf(ruleSet._id) >= 0);
              });
            }

            // Process rulesets
            rulesets = _.map(rulesets, (condition: any) => {
              if (condition._id === 'response_fields_logic_ruleset') {
                condition = this.getExternalFields(condition);
              }
              condition.id = condition._id;
              condition.text = condition.name;
              this.rulesetHash[condition._id] = condition;
              return condition;
            });

            // Extract fields and operators
            _.each(rulesets, (rule: any) => {
              const options: any = _.get(rule, 'schema.options');
              const operators: any = _.get(rule, 'schema.operators');
              const rulesetId = rule._id;

              if (!_.isEmpty(options)) {
                _.each(options, (fieldDetail: any, key: string) => {
                  const valueType = _.get(fieldDetail, 'value_type');
                  if ((!_.hasIn(fieldDetail, 'operators') || _.isEmpty(fieldDetail.operators)) &&
                    !_.isEmpty(operators) && _.has(operators, valueType) &&
                    !_.isEmpty(operators[valueType])) {
                    fieldDetail.operators = _.cloneDeep(operators[valueType]);
                  }
                  fieldDetail.id = fieldDetail.unique_id;
                  fieldDetail.text = fieldDetail.display_name;
                  fieldDetail.ruleset_id = rulesetId;
                  fieldDetail.ruleset_key = key;
                  this.workflowFields.push(fieldDetail);
                });
              }
            });

            resolve();
          },
          error: (error: any) => {
            console.error('Error fetching workflow rules:', error);
            resolve();
          }
        });
    });
  }


  /**
   * Get external fields for response_fields_logic_ruleset
   * Matching old ltms-workflow-edit-conditions-config getExternalFields
   */
  private getExternalFields(ruleSet: any): any {
    const addedItemsMap: any = {};
    if (ruleSet && ruleSet.schema && !_.isEmpty(ruleSet.schema.options)) {
      for (const fieldId in this.fieldDefsMap) {
        const fieldItem = this.fieldDefsMap[fieldId];
        if (fieldItem?.custom_parameter_type == 4) {
          if (fieldItem.group_id && !addedItemsMap[fieldItem.group_id]) {
            const groupField = this.fieldDefsMap[fieldItem.group_id];
            if (groupField && !groupField.group_id) {
              addedItemsMap[fieldItem.group_id] = 1;
              this.setRuleSetObj(this.fieldDefsMap[fieldItem.group_id], ruleSet);
            }
          }
          if (fieldItem.data_type === 'date') {
            this.setRuleSetObj(
              this.fieldDefsMap[fieldItem.field_id],
              ruleSet,
              (fieldItem.display_name || fieldItem.name)
            );
          }
        }
      }
    }
    return ruleSet;
  }

  /**
   * Matching old setRuleSetObj — inject business/calendar age fields for custom date fields
   */
  private setRuleSetObj(fieldItem: any, ruleSet: any, textToPrepend?: string): void {
    if (!ruleSet || !fieldItem) {
      return;
    }
    const group = this.fieldDefsMap[fieldItem.group_id];
    ['Business', ''].forEach((ageType) => {
      ['Hours', 'Minutes'].forEach((timeType) => {
        const sep = (ageType === 'Business') ? ' ' : '';
        let field_id: string;
        let name: string;
        let ruleSetObjectPath: string;

        if (textToPrepend) {
          let groupname = '';
          if (group && (group.name || group.display_name)) {
            groupname = group.display_name || group.name;
          }
          field_id = `response__has_${fieldItem.field_id}_diff_${ageType.toLowerCase()}_${timeType.toLowerCase()}`;
          name = `${groupname} - ${textToPrepend} (${ageType.trim()}${sep}${timeType})`;
          ruleSetObjectPath = `response.litmus_processed_data.${fieldItem.field_id}`;
        } else {
          field_id = `response__has_${fieldItem.field_id}_last_updated_${ageType.toLowerCase()}_${timeType.toLowerCase()}`;
          name = `Time Since Last ${(fieldItem.display_name || fieldItem.name)} Update (${ageType.trim()}${sep}${timeType})`;
          ruleSetObjectPath = `response.update_logs.${fieldItem.field_id}.last_updated_on`;
        }

        ruleSet.schema.options[field_id] = {
          name,
          should_evaluate: false,
          field_id: ruleSetObjectPath,
          display_name: name,
          value_type: 'date',
          unique_id: `response_fields_logic_ruleset___${field_id}`
        };
      });
    });
  }

  /**
   * Prepare workflow list - matching old AngularJS prepareWorkflowList
   */
  private prepareWorkflowList(rules: Workflow[]): void {
    this.workflows = [];
    this.workflowHash = {};

    if (rules && rules.length > 0) {
      // Format dates and add manager info - matching old AngularJS logic
      rules.forEach((rule: Workflow) => {
        // Format created date
        if (rule.created_date) {
          rule.formatted_created_date = moment(rule.created_date).format('DD-MMM-YY HH:mm');
          rule.createdDate = new Date(rule.created_date);
        }

        // Format last updated with manager info - matching old AngularJS logic
        if (rule.last_updated_on) {
          let lastUpdatedBy = moment(rule.last_updated_on).format('DD-MMM-YY HH:mm');
          // Matching old code: rules[i].last_updated_by (note: uses updated_by from rule, not managersDataMap check)
          if (rule.updated_by && this.managersMapping.has(rule.updated_by)) {
            lastUpdatedBy += ' by ' + this.managersMapping.get(rule.updated_by);
          }
          rule.last_updated_by = lastUpdatedBy;
          rule.lastUpdated = new Date(rule.last_updated_on);
          rule.updatedBy = rule.updated_by ? this.managersMapping.get(rule.updated_by) : '';
        } else {
          // If no last_updated_on, use created_date
          rule.last_updated_by = moment(rule.created_date).format('DD-MMM-YY HH:mm');
          rule.lastUpdated = new Date(rule.created_date);
        }

        // Set active alias for template
        rule.active = rule.is_enabled !== undefined ? rule.is_enabled : false;
      });

      // Sort by created date - matching old AngularJS logic
      rules = this.sortWorkflowByKey('created_date', rules, {
        is_date: true,
        asc: this.dateByAsc
      });

      // Filter out deleted workflows - matching old AngularJS logic
      this.completeWorkflow = _.filter(rules, (rule: Workflow) => {
        return !rule.is_deleted;
      });

      this.completeWorkflow = this.completeWorkflow.length > 0 ? this.completeWorkflow : [];
      this.pagination.statusRulesCount = this.completeWorkflow.length || 0;


      // Get page wise workflow
      this.getPageWiseWorkflow();

      // Create hash based on uuid - matching old AngularJS logic
      this.workflowHash = this.createHashBasedOnKey(rules, 'uuid');

      // Set workflowListToSearchInto for search - matching old AngularJS logic
      this.workflowListToSearchInto = _.cloneDeep(this.completeWorkflow);

    } else {
      console.warn('prepareWorkflowList: No rules provided or empty array');
      this.completeWorkflow = [];
      this.workflows = [];
      this.filteredWorkflows = [];
      this.workflowListToSearchInto = [];
      this.pagination.statusRulesCount = 0;
      this.pagination.statusRulesPageFrom = 0;
      this.pagination.statusRulesPageTo = 0;
      this.updatePagingButtons();
    }
  }

  /**
   * Get page wise workflow - matching old AngularJS getPageWiseWorkflow
   */
  private getPageWiseWorkflow(): void {
    const ruleList = _.cloneDeep(this.completeWorkflow);

    // Reset page number if complete workflow length equals limit - matching old AngularJS logic
    if (this.completeWorkflow && this.completeWorkflow.length === this.pagination.limit) {
      this.pagination.rulePageNo = 1;
    }

    const indexFrom = (this.pagination.rulePageNo - 1) * this.pagination.limit;
    this.workflows = ruleList.splice(indexFrom, this.pagination.limit);
    const workflowLength = this.workflows.length || 0;


    if (workflowLength && workflowLength === this.pagination.limit) {
      // If fetched workflows equals limit
      this.pagination.statusRulesPageFrom = ((this.pagination.rulePageNo - 1) * this.pagination.limit) + 1;
      this.pagination.statusRulesPageTo = indexFrom + this.pagination.limit;
      this.pagination.disablePrev = (this.pagination.statusRulesPageFrom === 1);
      this.pagination.disableNext = (this.pagination.statusRulesCount === this.pagination.statusRulesPageTo);
    } else if (workflowLength < this.pagination.limit) {
      // If fetched workflows is less than limit
      this.pagination.disableNext = true;
      this.pagination.disablePrev = (this.pagination.rulePageNo === 1);
      this.pagination.statusRulesPageFrom = ((this.pagination.rulePageNo - 1) * this.pagination.limit) + 1;
      this.pagination.statusRulesPageTo = indexFrom + workflowLength;
    } else {
      // No workflows
      this.pagination.disableNext = true;
      this.pagination.disablePrev = true;
      this.pagination.statusRulesPageFrom = 0;
      this.pagination.statusRulesPageTo = 0;
    }

    // Update pagination display values
    this.pagination.start = this.pagination.statusRulesPageFrom;
    this.pagination.end = this.pagination.statusRulesPageTo;
    this.pagination.total = this.pagination.statusRulesCount;
    this.pagination.totalPages = Math.ceil(this.pagination.statusRulesCount / this.pagination.limit) || 1;
    this.pagination.currentPage = this.pagination.rulePageNo;

    // Update filtered workflows for display - CRITICAL: This is what the template uses
    this.filteredWorkflows = [...this.workflows];
    this.updatePagingButtons();
  }

  private updatePagingButtons(): void {
    const pageNo = this.pagination.rulePageNo || 1;
    const totalPages = this.pagination.totalPages || 1;
    const hasData = (this.pagination.statusRulesCount || 0) > 0;
    const isDataLessThanLimit = this.workflows && this.workflows.length && (this.workflows.length <= this.pagination.limit);
    this.pagingButtons = {
      isFirst: (hasData && pageNo > 1 && isDataLessThanLimit) ? true : false,
      isPrevious: (hasData && pageNo > 1 && isDataLessThanLimit) ? true : false,
      isNext: (hasData && pageNo < totalPages && isDataLessThanLimit) ? true : false,
      isLast: (hasData && pageNo < totalPages && isDataLessThanLimit) ? true : false
    };
  }

  /**
   * Paginate workflows list - matching categories paging controls
   */
  paginateWorkflowsList(pagingType: string): void {
    const totalPages = this.pagination.totalPages || 1;

    if (pagingType === 'first') {
      this.pagination.rulePageNo = 1;
    } else if (pagingType === 'previous') {
      this.pagination.rulePageNo = Math.max(1, this.pagination.rulePageNo - 1);
    } else if (pagingType === 'next') {
      this.pagination.rulePageNo = Math.min(totalPages, this.pagination.rulePageNo + 1);
    } else if (pagingType === 'last') {
      this.pagination.rulePageNo = totalPages;
    }

    this.getPageWiseWorkflow();
  }

  /**
   * Get rules (pagination) - matching old AngularJS getRules
   */
  getRules(isNext: boolean): void {
    if (!isNext) {
      // Previous page
      this.pagination.rulePageNo--;
      if (this.pagination.rulePageNo === 1) {
        this.pagination.disablePrev = true;
      }
    } else {
      // Next page
      this.pagination.rulePageNo++;
    }
    this.getPageWiseWorkflow();
  }

  /**
   * Create hash based on key - matching old AngularJS createHashBasedOnKey
   */
  private createHashBasedOnKey(list: any[], key: string): any {
    const hash: any = {};
    _.each(list, (obj: any) => {
      hash[obj[key]] = obj;
    });
    return hash;
  }

  /**
   * Sort workflow by key - matching old AngularJS sortWorkflowByKey
   */
  private sortWorkflowByKey(key: string, list: any[], params?: any): any[] {
    try {
      let value: any = null;
      const defaultDate = new Date();
      defaultDate.setFullYear(defaultDate.getFullYear() - 10);

      const sortedList = _.sortBy(list, (workflow: any) => {
        if (params && params.is_date) {
          const date = new Date(workflow[key]).getTime();
          value = isNaN(date) ? new Date(defaultDate).getTime() : date;
        } else {
          value = workflow[key];
        }
        return (params && params.asc === true) ? -value : value;
      });

      const isToSortString = !(params && params.is_date) && (params && params.asc === true);
      return isToSortString ? sortedList.reverse() : sortedList;
    } catch (e) {
      return list;
    }
  }

  /**
   * Search workflow - matching old AngularJS searchWorkflow
   */
  searchWorkflow(textToSearch: string): void {
    this.lwActionWorkflowSearchInput = textToSearch;
    if (textToSearch && textToSearch.length) {
      const searchText = textToSearch.toLowerCase();
      this.workflows = _.filter(this.workflowListToSearchInto, (workflow: Workflow) => {
        return workflow.name.toLowerCase().indexOf(searchText) >= 0;
      });
      this.filteredWorkflows = [...this.workflows];
    } else {
      // Reset to page wise workflow
      this.getPageWiseWorkflow();
    }
  }

  /**
   * On search change
   */
  onSearchChange(): void {
    this.searchWorkflow(this.searchText);
  }

  /**
   * Toggle workflow (enable/disable) - matching old AngularJS enableWorkflow
   */
  toggleWorkflow(workflow: Workflow): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    this.pagerBusy = true;
    workflow.is_enabled = !workflow.is_enabled;
    workflow.active = workflow.is_enabled;
    workflow.last_updated_on = new Date();

    const param = {
      brandId: this.brandId,
      workflow: workflow
    };
    this.updateWorkflow(param);
  }

  /**
   * Update workflow - matching old AngularJS updateWorkflow
   */
  private updateWorkflow(param: any): void {
    this.pagerBusy = true;
    this._settingsService.updateWorkflow(param)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.code === 0) {
            this.resetData();
            this.getWorkflowList(this.brandId);
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Success', 'Workflow updated successfully.', 'Success');
            }
          } else if (response && response.code === 4022) {
            this.pagerBusy = false;
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Warning', 'Workflow already updated.', 'Success');
            }
          } else {
            this.pagerBusy = false;
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Error', 'Problem while updating workflow.', 'error', 3000);
            }
          }
        },
        error: (error: any) => {
          console.error('Error updating workflow:', error);
          this.pagerBusy = false;
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Problem while updating workflow.', 'error', 3000);
          }
        }
      });
  }

  /**
   * Edit workflow - matching old AngularJS openLtmsWorkflowModal (update mode)
   */
  editWorkflow(workflow: Workflow): void {
    if (!workflow || !workflow.uuid) {
      return;
    }

    this.modalMode = 'update';
    this.selectedWorkflow = _.cloneDeep(this.workflowHash[workflow.uuid] || workflow);
    this.indexOfWorkflowToEdit = _.findIndex(this.completeWorkflow, { uuid: workflow.uuid });

    this.modalSchema = this.getModalSchema();
    this.modalSchema.title = 'Edit Workflow';
    this.defaultValues = this.prepareWorkflowFormData(this.selectedWorkflow);
    this.isModalOpen = true;
  }

  /**
   * Add new workflow - matching old AngularJS openLtmsWorkflowModal (insert mode)
   */
  addNewWorkflow(): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    this.modalMode = 'insert';
    this.selectedWorkflow = null;
    this.indexOfWorkflowToEdit = -1;

    this.modalSchema = this.getModalSchema();
    this.modalSchema.title = 'Add Workflow';
    this.defaultValues = this.prepareWorkflowFormData(null);
    this.isModalOpen = true;
  }

  /**
   * Prepare form data for add/edit modal — matching old basic-config + edit-main bindings
   */
  private prepareWorkflowFormData(workflow: Workflow | null): any {
    const defaultCondition = this.getDataObject();
    defaultCondition.editing = true;
    const emptyConditionsV2 = {
      logical_operator: 'AND',
      uuid: this.generateGuid(),
      rules: []
    };

    if (!workflow) {
      const formData: any = {
        name: '',
        event_name: '',
        description: '',
        delay_time: 0,
        trigger_time_in_ms: 0,
        conditions: [_.cloneDeep(defaultCondition)],
        actions: []
      };
      if (this.conditionsV2Enabled) {
        formData.conditions_v2 = emptyConditionsV2;
      }
      return formData;
    }

    const formData: any = _.cloneDeep(workflow);
    formData.event_name = (workflow.event_id_list && workflow.event_id_list.length)
      ? workflow.event_id_list[0]
      : (workflow.event_name || '');

    // UI delay is minutes; API stores trigger_time_in_ms (old basic-config)
    const triggerMs = workflow.trigger_time_in_ms || 0;
    formData.trigger_time_in_ms = triggerMs;
    formData.delay_time = triggerMs ? (triggerMs / (60 * 1000)) : (workflow.delay_time || 0);
    formData.description = workflow.description || '';

    const existingConditions = (workflow.conditions && workflow.conditions.length)
      ? _.map(_.cloneDeep(workflow.conditions), (condition: any) => {
          condition.editing = false;
          return condition;
        })
      : [];

    // Keep one editable empty condition row for adding new conditions (v1 UI)
    formData.conditions = [...existingConditions, _.cloneDeep(defaultCondition)];
    formData.actions = workflow.actions ? _.cloneDeep(workflow.actions) : [];

    // Preserve conditions_v2 for brands with composite conditions enabled
    if (this.conditionsV2Enabled) {
      formData.conditions_v2 = workflow.conditions_v2
        ? _.cloneDeep(workflow.conditions_v2)
        : emptyConditionsV2;
    }

    // Clean display-only fields before binding
    delete formData.active;
    delete formData.formatted_created_date;
    delete formData.createdDate;
    delete formData.lastUpdated;
    delete formData.updatedBy;

    return formData;
  }

  private generateGuid(): string {
    if (Helpers && Helpers.guid) {
      return Helpers.guid();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getModalSchema() {
    const modalSchema: any = _.cloneDeep(this.workflowSchema);
    if (this.ltmsEventList && this.ltmsEventList.length > 0) {
      modalSchema.basic.properties.event_name.options = this.ltmsEventList;
    }
    // Event-change note when conditions v2 enabled (old basic-config warning)
    if (this.conditionsV2Enabled && modalSchema.basic?.properties?.event_name) {
      modalSchema.basic.properties.event_name.note =
        'Please Note: Changing the Event will reset the Conditions.';
    }
    modalSchema.conditions.properties.rulesetlist = [...this.workflowFields];
    modalSchema.conditions.properties.rulesetHash = { ...this.rulesetHash };
    modalSchema.conditions.properties.fieldDefsMap = { ...this.fieldDefsMap };
    modalSchema.conditionsV2Enabled = this.conditionsV2Enabled;
    return modalSchema;
  }

  getDataObject() {
    let dataObject: any = {};
    if (this.workflowSchema && _.hasIn(this.workflowSchema, 'conditions.properties.dataObject') && !_.isEmpty(this.workflowSchema.conditions.properties.dataObject)) {
      dataObject = _.cloneDeep(this.workflowSchema.conditions.properties.dataObject);
    }
    return dataObject;
  }

  /**
   * Matching old edit-main validateTabData across basic / conditions / actions
   */
  private validateWorkflowForm(formData: any): { isValid: boolean; errorMsg: string } {
    const errors: string[] = [];

    const basicResult = this.validateBasicTab(formData);
    if (!basicResult.isValid) {
      errors.push(basicResult.error || 'Invalid basic details.');
    }

    const conditionResult = this.validateConditionTab(formData);
    if (!conditionResult.isValid) {
      errors.push(conditionResult.error || 'Invalid conditions.');
    }

    const actionResult = this.validateActionTab(formData);
    if (!actionResult.isValid) {
      errors.push(actionResult.error || 'Invalid actions.');
    }

    return {
      isValid: errors.length === 0,
      errorMsg: errors.join('<br>')
    };
  }

  /** Matching old validateBasicTab + validateEventTab */
  private validateBasicTab(formData: any): { isValid: boolean; error?: string } {
    const eventValid = this.validateEventTab(formData);
    const hasName = !!(formData?.name && String(formData.name).trim());

    if (!hasName && !eventValid.isValid) {
      return { isValid: false, error: 'Name is mandatory.<br>' + eventValid.error };
    }
    if (!eventValid.isValid) {
      return { isValid: false, error: eventValid.error };
    }
    if (!hasName) {
      return { isValid: false, error: 'Name is mandatory.' };
    }

    const name = String(formData.name).trim().toLowerCase();
    const workflowWithSameName = _.filter(this.completeWorkflow, (workflow: Workflow) => {
      return (workflow.name || '').toLowerCase() === name;
    });

    let isSameWorkflow = false;
    if (this.modalMode === 'update' && this.indexOfWorkflowToEdit >= 0) {
      const editedWorkflow = this.completeWorkflow[this.indexOfWorkflowToEdit];
      isSameWorkflow = !!(editedWorkflow && workflowWithSameName.length &&
        _.get(workflowWithSameName, '0.uuid') === editedWorkflow.uuid); 
    }

    if (workflowWithSameName.length && !isSameWorkflow) {
      return { isValid: false, error: 'Workflow with same name already exists.' };
    }

    return { isValid: true };
  }

  private validateEventTab(formData: any): { isValid: boolean; error?: string } {
    const events = formData?.event_id_list?.length
      ? formData.event_id_list
      : (formData?.event_name ? [formData.event_name] : []);
    if (!events || !events.length) {
      return { isValid: false, error: 'Event is mandatory.' };
    }
    return { isValid: true };
  }

  /**
   * Matching old validateConditionTab — conditions optional; incomplete conditions_v2 rejected
   */
  private validateConditionTab(formData: any): { isValid: boolean; error?: string } {
    const rules = _.get(formData, 'conditions_v2.rules');
    if (rules && rules.length) {
      const conditionsV2Rules = this.removeDeletedRules(_.cloneDeep(rules));
      const validatedResult = this.validateRules(conditionsV2Rules);
      if (validatedResult && validatedResult.is_valid) {
        return { isValid: true };
      }
      return { isValid: false, error: 'One or more rules are incomplete' };
    }
    return { isValid: true };
  }

  private validateActionTab(formData: any): { isValid: boolean; error?: string } {
    const actions = formData?.actions;
    if (!actions || !actions.length) {
      return { isValid: false, error: 'Atleast one action is mandatory.' };
    }
    return { isValid: true };
  }

  /** Matching old validateRules — recursive completeness for conditions_v2 */
  private validateRules(rules: any[]): { rules: any[]; is_valid: boolean } {
    let is_valid = true;
    if (rules && rules.length) {
      _.each(rules, (rule: any) => {
        if (rule && rule.rules && rule.rules.length) {
          const validatedRules = this.validateRules(rule.rules);
          if (validatedRules.is_valid === false) {
            rule.is_valid = false;
            is_valid = false;
          }
        } else if (!rule.is_deleted) {
          if (rule.field_id && rule.operator_id && rule.value && rule.unique_id) {
            rule.is_valid = true;
          } else {
            rule.is_valid = false;
            is_valid = false;
          }
        }
      });
    }
    return { rules, is_valid };
  }

  /** Matching old removeDeletedRules */
  private removeDeletedRules(rules: any[]): any[] {
    return _.filter(rules, (c: any) => {
      delete c.is_valid;
      if (c.rules && c.rules.length) {
        c.rules = this.removeDeletedRules(c.rules);
      }
      return !c.is_deleted;
    });
  }

  /**
   * Matching old setDataType for flat conditions
   */
  private setConditionDataType(field: any): any {
    if ((field.value_type === 'number' || field.value_type === 'date') && !(isNaN(field.value))) {
      field.value = parseInt(field.value, 10);
    }
    if (this.fieldDefsMap && field.field_id === 'response_has_tag_data' && field.field_id_value) {
      const dataType = this.fieldDefsMap[field.field_id_value]?.data_type;
      field.value_type = (dataType && dataType !== 'list') ? dataType : 'string';
    }
    return field;
  }

  /**
   * Build API payload from form data — matching old editWorkflow / basic-config / insert+update cleanup
   */
  private buildWorkflowPayload(formData: any): any {
    const workflowPayload: any = _.cloneDeep(formData);
    const eventId = formData.event_name || (formData.event_id_list && formData.event_id_list[0]) || '';

    workflowPayload.name = (formData.name || '').trim();
    workflowPayload.description = formData.description || '';
    workflowPayload.event_id_list = eventId ? [eventId] : [];
    workflowPayload.actions = formData.actions || [];
    workflowPayload.last_updated_on = new Date();

    // Delay: UI minutes → trigger_time_in_ms (old changeTriggerTime)
    const delayMinutes = Number(formData.delay_time) || 0;
    workflowPayload.trigger_time_in_ms = delayMinutes > 0 ? (delayMinutes * 60 * 1000) : 0;

    // Flat conditions with datatype coercion
    let conditions = formData.conditions || [];
    conditions = _.map(conditions, (c: any) => this.setConditionDataType(_.cloneDeep(c)));
    workflowPayload.conditions = conditions;

    // conditions_v2: strip soft-deleted rules (old removeDeletedRules on insert/update)
    if (workflowPayload.conditions_v2?.rules?.length) {
      workflowPayload.conditions_v2.rules = this.removeDeletedRules(
        _.cloneDeep(workflowPayload.conditions_v2.rules)
      );
    } else if (this.conditionsV2Enabled && this.selectedWorkflow?.conditions_v2 && this.modalMode === 'update') {
      // Preserve existing conditions_v2 if form didn't touch it
      const preserved = _.cloneDeep(this.selectedWorkflow.conditions_v2);
      if (preserved?.rules?.length) {
        preserved.rules = this.removeDeletedRules(preserved.rules);
      }
      workflowPayload.conditions_v2 = preserved;
    }

    // Remove form-only / display-only fields
    delete workflowPayload.event_name;
    delete workflowPayload.delay_time;
    delete workflowPayload.active;
    delete workflowPayload.formatted_created_date;
    delete workflowPayload.createdDate;
    delete workflowPayload.lastUpdated;
    delete workflowPayload.updatedBy;

    return workflowPayload;
  }

  /**
   * Matching old updateWorkflow no-change detection (incl. conditions_v2 cleanup before compare)
   */
  private hasWorkflowChanges(updatedWorkflow: any, originalWorkflow: any): boolean {
    if (!originalWorkflow) {
      return true;
    }

    const paramWorkflow = _.cloneDeep(updatedWorkflow);
    if (paramWorkflow.conditions_v2?.rules) {
      paramWorkflow.conditions_v2.rules = this.removeDeletedRules(paramWorkflow.conditions_v2.rules);
    }

    const editedWorkflow = _.cloneDeep(originalWorkflow);
    const skipKeys = ['active', 'formatted_created_date', 'createdDate', 'lastUpdated', 'updatedBy', 'last_updated_by', 'last_updated_on', 'event_name', 'delay_time'];

    if (_.keys(editedWorkflow).length !== _.keys(paramWorkflow).length) {
      // Still compare meaningful keys — length alone can differ due to display fields
    }

    let noChange = true;
    _.each(_.keys(paramWorkflow), (key: string) => {
      if (skipKeys.includes(key)) {
        return;
      }
      if (paramWorkflow[key] || paramWorkflow[key] === 0) {
        let left = paramWorkflow[key];
        let right = editedWorkflow[key];

        if (key === 'conditions_v2' && left?.rules) {
          left = { ...left, rules: this.removeDeletedRules(_.cloneDeep(left.rules)) };
        }

        if (!Array.isArray(left) && !_.isEqual(left, right)) {
          noChange = false;
        } else if (Array.isArray(left)) {
          _.each(left, (arrayItem: any) => {
            if (arrayItem && typeof arrayItem === 'object') {
              delete arrayItem.$$hashKey;
              delete arrayItem.displayText;
              delete arrayItem.editing;
            }
          });
          _.each(right, (editedArrayItem: any) => {
            if (editedArrayItem && typeof editedArrayItem === 'object') {
              delete editedArrayItem.$$hashKey;
              delete editedArrayItem.displayText;
              delete editedArrayItem.editing;
            }
          });
          if (!_.isEqual(left, right)) {
            noChange = false;
          }
        }
      }
    });

    return !noChange;
  }

  /**
   * Insert workflow - matching old AngularJS insertWorkflow
   */
  private insertWorkflow(workflow: any): void {
    this.pagerBusy = true;
    workflow.is_enabled = false;
    workflow.brand_id = this.brandId;

    this._settingsService.insertWorkflow(workflow)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.code === 0) {
            this.closeModal();
            this.resetData();
            this.getWorkflowList(this.brandId);
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Success', 'Rule saved successfully, please activate from the list.', 'Success');
            }
          } else {
            this.pagerBusy = false;
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Error', 'Problem while creating workflow.', 'error', 3000);
            }
          }
        },
        error: (error: any) => {
          console.error('Error inserting workflow:', error);
          this.pagerBusy = false;
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Problem while creating workflow.', 'error', 3000);
          }
        }
      });
  }

  /**
   * Save updated workflow - matching old AngularJS update path in edit modal
   */
  private saveUpdatedWorkflow(workflow: any): void {
    const param = {
      brandId: this.brandId,
      workflow
    };
    this._settingsService.updateWorkflow(param)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.code === 0) {
            this.closeModal();
            this.resetData();
            this.getWorkflowList(this.brandId);
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Success', 'Workflow updated successfully.', 'Success');
            }
          } else if (response && response.code === 4022) {
            this.pagerBusy = false;
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Warning', 'Workflow already updated.', 'Success');
            }
          } else {
            this.pagerBusy = false;
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Warning', 'No changes made.', 'warn', 3000);
            }
          }
        },
        error: (error: any) => {
          console.error('Error updating workflow:', error);
          this.pagerBusy = false;
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Problem while updating workflow.', 'error', 3000);
          }
        }
      });
  }

  /**
   * Delete workflow - matching old AngularJS deleteWorkflow
   */
  deleteWorkflow(workflow: Workflow): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }

    if (!workflow || !workflow.uuid) {
      return;
    }

    const performDelete = () => {
      const param = {
        brandId: this.brandId,
        uuid: workflow.uuid
      };
      this.pagerBusy = true;
      this._settingsService.deleteWorkflow(param)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            if (response && response.code === 0) {
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Success', 'Workflow deleted successfully.', 'Success');
              }
              this.resetData();
              this.getWorkflowList(this.brandId);
            } else {
              this.pagerBusy = false;
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Error', 'Problem while deleting workflow.', 'error');
              }
            }
          },
          error: (error: any) => {
            console.error('Error deleting workflow:', error);
            this.pagerBusy = false;
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Error', 'Problem while deleting workflow.', 'error');
            }
          }
        });
    };

    if (Helpers && Helpers.showConfirmationBox) {
      Helpers.showConfirmationBox('Confirmation', 'Are you sure you want to delete this rule?', (answer: boolean) => {
        if (answer) {
          performDelete();
        }
      });
    } else {
      const confirmed = confirm('Are you sure you want to delete this rule?');
      if (confirmed) {
        performDelete();
      }
    }
  }

  /**
   * Previous page - for template compatibility
   */
  previousPage(): void {
    if (this.pagination.rulePageNo > 1) {
      this.getRules(false);
    }
  }

  /**
   * Next page - for template compatibility
   */
  nextPage(): void {
    if (!this.pagination.disableNext) {
      this.getRules(true);
    }
  }

  /**
   * Save configuration - matching old AngularJS logic (no save needed, changes are immediate)
   */
  saveConfiguration(): void {
    // In the old AngularJS component, changes are saved immediately (toggle, delete, etc.)
    // There's no separate save button functionality
    // This method is kept for template compatibility but doesn't do anything
    if (Helpers && Helpers.smallAlertBox) {
      Helpers.smallAlertBox('Info', 'Workflow changes are saved automatically.', 'info');
    }
  }

  /**
   * Cancel configuration - matching old AngularJS logic
   */
  cancelConfiguration(): void {
    // In the old AngularJS component, there's no cancel functionality
    // Changes are immediate, so cancel just refreshes the data
    if (this.isCancelling) return;

    this.isCancelling = true;

    // Refresh workflow list to reset any local changes
    this.resetData();
    this.getWorkflowList(this.brandId);

    setTimeout(() => {
      this.isCancelling = false;
    }, 500);
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    // Since changes are saved immediately, there are no unsaved changes
    return true;
  }

  onFormSubmit(event: any): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    const formData = _.cloneDeep(event || {});
    const validation = this.validateWorkflowForm(formData);
    if (!validation.isValid) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Error', validation.errorMsg, 'error', 3000);
      }
      return;
    }

    const workflowPayload = this.buildWorkflowPayload(formData);

    if (this.modalMode === 'update') {
      const originalWorkflow = (this.indexOfWorkflowToEdit >= 0)
        ? this.completeWorkflow[this.indexOfWorkflowToEdit]
        : this.selectedWorkflow;

      if (!this.hasWorkflowChanges(workflowPayload, originalWorkflow)) {
        if (Helpers && Helpers.smallAlertBox) {
          Helpers.smallAlertBox('Warning', 'No changes made.', 'warn', 3000);
        }
        return;
      }

      if (Helpers && Helpers.showConfirmationBox) {
        Helpers.showConfirmationBox(
          'Confirmation',
          'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?',
          (answer: boolean) => {
            if (answer) {
              this.saveUpdatedWorkflow(workflowPayload);
            }
          }
        );
      } else {
        const confirmed = confirm('Please note that this change will Override the previous configuration. Are you sure, you want to proceed?');
        if (confirmed) {
          this.saveUpdatedWorkflow(workflowPayload);
        }
      }
    } else {
      this.insertWorkflow(workflowPayload);
    }
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.modalMode = 'insert';
    this.selectedWorkflow = null;
    this.indexOfWorkflowToEdit = -1;
    this.defaultValues = {};
    this.modalSchema = {};
  }



}
